import React from "react";
import AppointmentDrawer, {
  AppointmentNested,
} from "../appointment/AppointmentDrawer";
import { transactionFlags } from "../flags/calendarFlags";
import { useSalon } from "../../../../context/SalonContext";
import { badgeSettingsFrom } from "../../../../utils/badgeSettings";
import type { DrawerSlotApi } from "../drawer/drawerSlot";
import ClientDetailsDrawer from "../../../clients/ClientDetailsDrawer";
import BlurLoadingOverlay from "../../../common/BlurLoadingOverlay";
import useReceiptPrint from "../../../common/useReceiptPrint";
import { TemplateLibraryModal } from "../../../formTemplates/TemplateLibraryModal";
import { FormViewer } from "../../../formTemplates/viewer/FormViewer";
import { PreviewPopoverState } from "../hooks/usePreviewPopover";
import { GetTransactionsResponse } from "../../../../types/transactions";

interface PreviewSurfacesProps {
  preview: PreviewPopoverState;
  salonUuid: string | undefined;
  /** Salon IANA timezone (status gates such as Arrived are judged in it). */
  timezone: string;
  /** Rebook / Reschedule open the booking drawer (booking session). */
  onRebook: (transaction: GetTransactionsResponse) => void;
  onReschedule: (transaction: GetTransactionsResponse) => void;
  /** The drawer slot: the appointment drawer's nested screen lives on it. */
  drawerSlot: Pick<DrawerSlotApi, "slot" | "openNested" | "closeNested">;
}

/**
 * The appointment drawer plus the sub-surfaces it opens (client drawer,
 * Add Form/SOAP library, FormViewer). All callbacks come from
 * usePreviewPopover (the drawer's state hook) and are stable.
 */
const PreviewSurfaces: React.FC<PreviewSurfacesProps> = ({
  preview,
  salonUuid,
  timezone,
  onRebook,
  onReschedule,
  drawerSlot,
}) => {
  const badgeSettings = badgeSettingsFrom(
    (useSalon() as { businessInfo?: unknown }).businessInfo,
  );
  // The drawer's printer icon prints through this single hidden receipt.
  const { printReceipt, receipt } = useReceiptPrint();
  return (
    <>
      {receipt}
      {/* Appointment drawer (drawer slot kind "appointment") */}
      <AppointmentDrawer
        previewData={preview.previewData}
        salonUuid={salonUuid}
        timezone={timezone}
        onClose={preview.closePreview}
        nested={
          drawerSlot.slot?.kind === "appointment"
            ? drawerSlot.slot.nested
            : null
        }
        onOpenNested={(n: AppointmentNested) => drawerSlot.openNested(n)}
        onCloseNested={drawerSlot.closeNested}
        onCheckout={preview.handlePreviewEdit}
        onOpenForm={preview.handleOpenForm}
        onRebook={onRebook}
        onReschedule={onReschedule}
        onStatusUpdate={preview.handlePreviewStatusUpdate}
        onAddForm={preview.handleAddForm}
        onFormsChange={preview.handlePreviewFormsChange}
        onTagsChange={preview.handlePreviewTagsChange}
        formsAlreadySent={preview.formsAlreadySent}
        onFormsSent={preview.markFormsSent}
        onViewClient={preview.handleViewClient}
        flags={
          preview.previewData?.transaction
            ? transactionFlags(
                preview.previewData.transaction,
                timezone,
                badgeSettings,
              )
            : []
        }
        badgeSettings={badgeSettings}
        onPrintReceipt={printReceipt}
      />

      {/* Client drawer (opened from the preview popover — client name or notes).
        Closing it re-opens the drawer. */}
      <ClientDetailsDrawer
        client={preview.notesClient}
        opened={preview.notesDrawerOpen}
        onClose={preview.handleClientDrawerClose}
        initialTab={preview.clientDrawerTab}
        hideDeleteButton
        onOpenTransaction={preview.handleOpenTimelineTransaction}
      />

      <BlurLoadingOverlay visible={preview.timelineTxnLoading} />

      {/* "Add Form/SOAP" template library — lifted out of the popover so it
        survives the drawer closing; closing it re-opens the drawer. */}
      <TemplateLibraryModal
        opened={preview.addFormModalOpen}
        onClose={preview.dismissAddFormModal}
        onUseTemplate={preview.handleUseTemplate}
        onEditCustom={preview.dismissAddFormModal}
        onCreateNew={preview.dismissAddFormModal}
        canManageTemplates={false}
        canManageClientForms
        isAssigning={preview.isAssigningForm}
      />

      {/* FormViewer opened from a popover form row — lifted out of the popover
        so it survives the drawer closing; closing it re-opens the drawer. */}
      {preview.viewFormTransaction &&
        preview.viewFormId &&
        salonUuid &&
        preview.viewFormTransaction.client?.id && (
          <FormViewer
            salonId={salonUuid}
            clientId={preview.viewFormTransaction.client.id}
            formId={preview.viewFormId}
            onChanged={preview.handleFormViewerChanged}
            onClose={preview.handleFormViewerClose}
          />
        )}
    </>
  );
};

export default PreviewSurfaces;
